/**
 * Send a GA4 `page_view` on mount and on every client-side route change.
 *
 * WHY THIS HAS TO EXIST AT ALL. `createGoogleAnalyticsProvider` configures GA4
 * with `send_page_view: false` — deliberately, because gtag's automatic view
 * fires once per DOCUMENT and these are SPAs, where the document never changes.
 * The provider's contract is therefore "the caller sends views." The consent
 * path (`mountConsentedAnalytics`) has always honoured that; `mountAnalytics`
 * in client.tsx did NOT. It called `initGA4` and stopped.
 *
 * The result was analytics that looked wired from every angle and measured
 * nothing: gtag/js loads 200, `window.gtag` exists, dataLayer holds a real
 * `config` command — and not one `/g/collect` hit is ever sent, so the property
 * reports zero traffic forever. Measured on holytrinitycovina.com 2026-08-19,
 * minutes after its measurement id went live.
 *
 * That is the same failure family as the `arguments`-vs-rest-array bug recorded
 * in the provider: the tag installs, the page looks instrumented, and the number
 * at the other end is zero. Checking that GA4 "is on" cannot mean checking that
 * the script loaded.
 *
 * MOUNTED AFTER the app, not before it (see client.tsx). React flushes passive
 * effects in tree order, so a tracker rendered ahead of the app would read
 * `document.title` from the page the visitor just LEFT — every hit attributed
 * one navigation late. Rendering it last lets the page's own title effect win
 * first. `page_path` is unaffected either way; `page_title` is the field that
 * silently rots.
 *
 * No-ops entirely until `initGA4` has run with a real measurement id, so an org
 * with no id configured (most of them) stays untracked and cookie-free.
 */
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { analyticsEnabled, trackPageview } from '@gjl/analytics'

export function PageviewTracker() {
  const { pathname, search } = useLocation()

  useEffect(() => {
    if (!analyticsEnabled()) return
    trackPageview(pathname + search)
  }, [pathname, search])

  return null
}
